import * as React from 'react';
import { observer } from 'mobx-react';
import { Row, Col } from 'antd';
import * as uuid from 'uuid';
import autobind from 'autobind-decorator';

import { TextView } from 'src/core/ui/text.view';
import { CardController } from 'src/app/shared/card.controller';
import { DataTableView, ToolbarVisibility, Column } from 'src/core/ui/data-table.view';
import { DataLocalService } from 'src/core/services/data-local.service';

interface Props extends ComponentViewProps<TourItineraryDateItem> {
	mode: ControlMode;
	invalidOccupancies: Set<string>;
	getAgeBandTag: (item: AgeBandItem) => string;
	onChangeOccupancyValidity: (value: boolean) => void;
	ensureOccupancyConstraints: (newPaxPerBooking?: number) => void;
}

@observer
export class TourItineraryDatePaxConfig extends React.Component<Props, {}> {
	private getColumns(): Column[] {
		const { mode, getAgeBandTag } = this.props;
		const ageBands: AgeBandItem[] = this.props.model.dynamicProperties.ageBands;

		return ageBands
			.map((m) => {
				const title = m.label;
				const key = getAgeBandTag(m);

				return {
					key: key,
					title: title,
					dataIndex: key,
					editable: (field: number, record: CustomOccupancyPax, index: number, isSelected: boolean) =>
						isSelected
							? (
								<TextView
									required
									mode={mode}
									label={null}
									value={field}
									onChanged={(newValue) => {
										record[key] = newValue;
									}}
								/>
							)
							: (field != null ? field : '')

				};
			});
	}

	@autobind
	private generateAllOccupancies(): void {
		const { model, getAgeBandTag } = this.props;
		const ageBands: AgeBandItem[] = model.dynamicProperties.ageBands;
		const paxConfig: ItineraryDatePaxConfig = model.dynamicProperties.paxConfig;
		const { maxPaxPerBooking } = paxConfig;

		const comb = function* (pos: number, occupancy: number[], residualCapacity: number) {
			if (pos == occupancy.length || residualCapacity === 0) {
				if (occupancy.some(m => m !== 0)) {
					yield [...occupancy];
				}
				return;
			}

			for (let i = 0; i <= residualCapacity; i++) {
				occupancy[pos] = i;
				yield* comb(pos + 1, occupancy, residualCapacity - i);
				occupancy[pos] = 0;
			}
		};

		paxConfig.occupancies = Array
			.from(comb(0, new Array(ageBands.length), maxPaxPerBooking))
			.map(m =>
				ageBands.reduce((prev, curr, index) => ({ ...prev, [getAgeBandTag(curr)]: m[index].toString() }), { id: uuid.v4() })
			);
	}

	@autobind
	private async handleCreateOccupancy(): Promise<CustomOccupancyPax> {
		const { model, getAgeBandTag, onChangeOccupancyValidity } = this.props;
		const ageBands: AgeBandItem[] = model.dynamicProperties.ageBands;
		const paxConfig: ItineraryDatePaxConfig = model.dynamicProperties.paxConfig;

		const customOccupancy: CustomOccupancyPax = ageBands.reduce((prev, curr) => ({ ...prev, [getAgeBandTag(curr)]: '0' }), { id: uuid.v4() });
		paxConfig.occupancies.push(customOccupancy);
		onChangeOccupancyValidity(false);

		return customOccupancy;
	}

	@autobind
	private handleItemDoubleClick(): void {
		this.props.onChangeOccupancyValidity(false);
	}

	@autobind
	private afterDeleteOccupancies(items?: AgeBandItem[]): void {
		this.props.ensureOccupancyConstraints();
	}

	@autobind
	private async isValidOccupancy(weakCopy: CustomOccupancyPax): Promise<string[]> {
		const errors = [];
		const { model, getAgeBandTag, ensureOccupancyConstraints } = this.props;
		const ageBands: AgeBandItem[] = model.dynamicProperties.ageBands;
		const paxConfig: ItineraryDatePaxConfig = model.dynamicProperties.paxConfig;
		const { maxPaxPerBooking } = paxConfig;

		const item = paxConfig.occupancies.filter(m => m.id === weakCopy.id)[0];
		const sum = ageBands.reduce((prev, curr) => prev + parseInt(item[getAgeBandTag(curr)]), 0)
		if (sum > parseInt(maxPaxPerBooking.toString())) {
			errors.push(`The capacity (Maximum Pax Per Booking) is exceeded.`);
		}

		const isEmptyOccupancy = ageBands.every(m => parseInt(item[getAgeBandTag(m)]) === 0);
		if (isEmptyOccupancy) {
			errors.push("The occupancy can't be empty.");
		}

		const isRepeated = paxConfig.occupancies
			.filter(m => ageBands.every(p => parseInt(m[getAgeBandTag(p)]) === parseInt(item[getAgeBandTag(p)])))
			.length > 1;
		if (isRepeated) {
			errors.push('The configuration has been set.');
		}

		if (errors.length === 0) {
			ensureOccupancyConstraints();
		}

		return errors;
	}

	@autobind
	private getClassName(record: CustomOccupancyPax): string {
		const { invalidOccupancies } = this.props;
		return invalidOccupancies.has(record.id) ? 'invalid' : '';
	}

	render(): React.ReactNode {
		const { model, mode, ensureOccupancyConstraints, setBusy } = this.props;
		const paxConfig: ItineraryDatePaxConfig = model.dynamicProperties.paxConfig;
		const occupanciesDataSet: any = new DataLocalService(paxConfig.occupancies);

		return (
			<React.Fragment>
				<Row gutter={16}>
					<Col xs={6}>
						<TextView
							required
							mode={mode}
							label="Min Pax"
							targetType="int+"
							value={paxConfig.min}
							onChanged={(newValue) => {
								paxConfig.min = newValue;
							}}
						/>
					</Col>
					<Col xs={6}>
						<TextView
							required
							mode={mode}
							label="Max pax"
							targetType="int+"
							value={paxConfig.max}
							onChanged={(newValue) => {
								paxConfig.max = newValue
							}}
						/>
					</Col>
					<Col xs={6}>
						<TextView
							required
							mode={mode}
							targetType="int+"
							label="Default pax per booking"
							value={paxConfig.defaultPaxPerBooking}
							onChanged={(newValue) => {
								paxConfig.defaultPaxPerBooking = newValue;
							}}
						/>
					</Col>
					<Col xs={6}>
						<TextView
							required
							mode={mode}
							targetType="int+"
							label="Max pax per booking"
							value={paxConfig.maxPaxPerBooking}
							onChanged={(newValue) => {
								paxConfig.maxPaxPerBooking = newValue;
								ensureOccupancyConstraints(newValue);
							}}
						/>
					</Col>
				</Row>
				<Row>
					<CardController
						title="Pax config"
						icon="book"
						className="card-borderless"
						mode={mode}>
						<DataTableView
							mode={mode}
							columns={this.getColumns()}
							model={occupanciesDataSet}
							isValidItem={this.isValidOccupancy}
							itemDoubleClick={this.handleItemDoubleClick}
							createNewItem={this.handleCreateOccupancy}
							afterDeleteItem={this.afterDeleteOccupancies}
							rowKey={(m) => m.id}
							rowClassName={this.getClassName}
							setBusy={setBusy}
							toolbarLeftItems={[
								{
									key: 'generate-all',
									disabled: mode === 'display',
									onClick: () => this.generateAllOccupancies(),
									name: 'All Ocuppancies'
								}
							]}
							hideToolbar={ToolbarVisibility.hideFooter | ToolbarVisibility.hidePagination | ToolbarVisibility.hideSearch}
						/>
					</CardController>
				</Row>
			</React.Fragment>
		);
	}
}



// WEBPACK FOOTER //
// ./src/app/products/tour-itineraries/tour-itinerary-date/form/parts/tour-itinerary-date-pax-config.tsx